// src/pages/clients/ClientCreate.js - AVEC LOGS DÉTAILLÉS
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import countryCodes from "../data/countryCodes"; // Import des codes pays

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const ClientCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState("");
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  
  // États pour l'erreur de quota
  const [quotaError, setQuotaError] = useState({
    show: false,
    type: "",
    message: "",
    utilises: 0,
    total: 0,
    restant: 0
  });
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    nom_client: "",
    prenom_client: "",
    tel_client: "",
    email_client: "",
    type_client: "1", // 1=Entreprise, 2=Particulier
    date_debut_contrat_client: "",
    date_fin_contrat_client: "",
    piece_identite_client: "",
    nif_client: "",
    rccm_client: "",
    statut_client: "1", // 1=Actif par défaut
    jours_rappel_fin_contrat: "5"
  });
  
  const [userInfo, setUserInfo] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState(null); // { utilises, total, restant, pourcentage }

  // États pour le sélecteur d'indicatif téléphonique
  const [showCountryList, setShowCountryList] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes?.find(c => c.code === "+227") || countryCodes?.[0] || { 
      code: "+227", 
      name: "Niger", 
      flag: "🇳🇪",
      phoneLength: 8,
      pattern: "^\\+227\\d{8}$"
    }
  );
  const [searchCountry, setSearchCountry] = useState("");
  const countryListRef = useRef(null);

  // Références pour fermer le dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryListRef.current && !countryListRef.current.contains(event.target)) {
        setShowCountryList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos utilisateur et vérifier les permissions
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        console.log("📋 ClientCreate - User data:", userData);
        
        // Normalisation
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0
        };
        
        setUserInfo(normalizedData);
        
        // Vérifier les permissions
        const userProfile = String(normalizedData.profile || "").trim();
        if (userProfile !== "3") {
          console.log("❌ Utilisateur n'est pas gestionnaire, redirection...");
          navigate("/clients");
        }
      } catch (error) {
        console.error("❌ Error parsing user:", error);
        navigate("/login");
      }
    } else {
      console.log("❌ Aucun utilisateur trouvé");
      navigate("/login");
    }
    setCheckingPermissions(false);
  }, [navigate]);

  // Quota "clients" restant selon l'abonnement (affiché avant même de soumettre
  // le formulaire, en complément du message d'erreur réactif ci-dessous).
  useEffect(() => {
    if (!userInfo?.societe_id) return;
    const token = localStorage.getItem("access_token");
    axios.get(`${API_BASE_URL}/achat_quota/get_restant.php?id_societe=${userInfo.societe_id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (res.data?.success && res.data.quotas?.clients) {
          setQuotaInfo(res.data.quotas.clients);
        }
      })
      .catch(() => {});
  }, [userInfo]);

  // Ajuster les champs obligatoires selon le type
  useEffect(() => {
    if (formData.type_client === "2") { // Particulier
      // Pour un particulier, NIF et RCCM deviennent optionnels
    } else if (formData.type_client === "1") { // Entreprise
      // Pour une entreprise, NIF et RCCM deviennent obligatoires
    }
  }, [formData.type_client]);

  /* ====== FONCTIONS POUR LE TÉLÉPHONE ====== */
  
  // Fonction pour extraire les chiffres
  const extractPhoneDigits = (phoneValue) => {
    if (!phoneValue) return "";
    return phoneValue.replace(/\D/g, "");
  };

  // Fonction pour formater avec indicatif
  const formatFullPhoneNumber = (phoneDigits, countryCode = "+227") => {
    if (!phoneDigits) return "";
    return countryCode + phoneDigits;
  };

  // Fonction pour générer exemple
  const generatePhoneExample = (phoneLength) => {
    if (!phoneLength) return "";
    let example = "";
    for (let i = 0; i < phoneLength; i++) {
      example += (i % 10).toString();
    }
    return example;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Pour le champ téléphone, n'accepter que les chiffres
    if (name === "tel_client") {
      const maxLength = selectedCountry?.phoneLength || 15;
      const digitsOnly = value.replace(/\D/g, "");
      const limitedDigits = digitsOnly.substring(0, maxLength);
      
      setFormData(prev => ({ ...prev, [name]: limitedDigits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Effacer les erreurs quand l'utilisateur modifie
    if (errors.length > 0) {
      setErrors([]);
    }
    // Effacer l'erreur de quota
    if (quotaError.show) {
      setQuotaError({ show: false });
    }
  };

  /* ====== COMPOSANT COUNTRYSELECTOR ====== */
  const CountrySelector = ({ 
    showList, setShowList, selectedCountry, setSelectedCountry, 
    search, setSearch, listRef, disabled 
  }) => {
    // Filtrer les pays de manière SÉCURISÉE
    const filteredCountries = (countryCodes || []).filter(country => {
      if (!country) return false;
      
      const searchTerm = (search || "").toLowerCase();
      const countryName = String(country.name || country.label || "").toLowerCase();
      const countryCode = String(country.code || "").toLowerCase();
      
      return countryName.includes(searchTerm) || countryCode.includes(searchTerm);
    });

    return (
      <div ref={listRef} className="position-relative">
        <button
          type="button"
          className="btn d-flex align-items-center justify-content-between"
          onClick={() => setShowList(!showList)}
          disabled={disabled}
          style={{
            backgroundColor: '#F5F7FA',
            border: `1px solid #e0e0e0`,
            borderRadius: '8px',
            padding: '8px 12px',
            width: '120px',
            minHeight: '44px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
        >
          <span className="me-2" style={{ fontSize: '1.2rem' }}>{selectedCountry?.flag || "🏳️"}</span>
          <span style={{ fontSize: '0.9rem', color: '#2E2E2E', fontWeight: '500' }}>
            {selectedCountry?.code || "+227"}
          </span>
          <i className="bi bi-chevron-down ms-2" style={{ fontSize: '0.8rem', color: '#666' }}></i>
        </button>

        {showList && !disabled && (
          <div style={{
            position: 'absolute', top: '100%', left: '0', width: '280px', maxHeight: '300px',
            backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden', marginTop: '4px'
          }}>
            <div className="p-2 border-bottom">
              <div className="input-group">
                <span className="input-group-text" style={{ 
                  backgroundColor: '#F5F7FA', borderColor: '#e0e0e0', padding: '8px 12px'
                }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un pays..."
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: '0.9rem', borderColor: '#e0e0e0', padding: '8px 12px' }}
                  autoFocus
                />
              </div>
            </div>

            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredCountries.map((country, index) => {
                if (!country) return null;
                
                return (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-light text-start w-100 border-0 rounded-0"
                    onClick={() => {
                      if (country) {
                        setSelectedCountry(country);
                        setShowList(false);
                        setSearch("");
                      }
                    }}
                    style={{ 
                      padding: '10px 16px', 
                      borderBottom: '1px solid #f0f0f0', 
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-3" style={{ fontSize: '1.2rem', minWidth: '30px' }}>
                        {country?.flag || "🏳️"}
                      </span>
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: '500' }}>
                          {country?.name || country?.label || "Pays"}
                        </div>
                        <small style={{ color: '#666' }}>{country?.code || ""}</small>
                        {country?.phoneLength && (
                          <small style={{ color: '#666', display: 'block' }}>
                            {country.phoneLength} chiffres
                          </small>
                        )}
                      </div>
                      {selectedCountry?.code === country?.code && <i className="bi bi-check text-primary"></i>}
                    </div>
                  </button>
                );
              })}
              
              {filteredCountries.length === 0 && (
                <div className="text-center py-3 text-muted" style={{ fontSize: '0.9rem' }}>
                  Aucun pays trouvé
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const validateForm = () => {
    const validationErrors = [];
    
    // Champs obligatoires pour tous
    if (!formData.nom_client.trim()) {
      validationErrors.push("Le nom du client est obligatoire");
    }
    
    if (!formData.tel_client.trim()) {
      validationErrors.push("Le téléphone du client est obligatoire");
    } else {
      // Validation téléphone - DYNAMIQUE PAR PAYS
      const phoneDigits = extractPhoneDigits(formData.tel_client);
      const expectedLength = selectedCountry?.phoneLength;
      
      if (!/^\d+$/.test(phoneDigits)) {
        validationErrors.push("Le numéro de téléphone doit contenir uniquement des chiffres");
      } else if (expectedLength && phoneDigits.length !== expectedLength) {
        validationErrors.push(`Numéro ${selectedCountry?.name || ""} invalide (${phoneDigits.length} chiffres, besoin de ${expectedLength} chiffres)`);
      } else if (!expectedLength && (phoneDigits.length < 8 || phoneDigits.length > 15)) {
        validationErrors.push(`Numéro invalide (${phoneDigits.length} chiffres, doit être entre 8 et 15 chiffres)`);
      }
    }
    
    if (!formData.date_debut_contrat_client) {
      validationErrors.push("La date de début de contrat est obligatoire");
    }
    
    if (!formData.date_fin_contrat_client) {
      validationErrors.push("La date de fin de contrat est obligatoire");
    }
    
    // Validation des dates
    if (formData.date_debut_contrat_client && formData.date_fin_contrat_client) {
      const debut = new Date(formData.date_debut_contrat_client);
      const fin = new Date(formData.date_fin_contrat_client);
      
      if (fin <= debut) {
        validationErrors.push("La date de fin doit être postérieure à la date de début");
      }
    }

    if (formData.jours_rappel_fin_contrat < 1 || formData.jours_rappel_fin_contrat > 30) {
      validationErrors.push("Le nombre de jours pour le rappel doit être entre 1 et 30");
    }
    
    // Validation selon le type
    if (formData.type_client === "2") { // Particulier
      // Nom/prénom d'une personne physique : lettres uniquement (contrairement
      // au nom d'une entreprise, qui peut légitimement contenir des chiffres).
      const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
      if (formData.nom_client.trim() && !nameRegex.test(formData.nom_client.trim())) {
        validationErrors.push("Le nom ne doit contenir que des lettres pour un particulier");
      }
      if (!formData.prenom_client.trim()) {
        validationErrors.push("Le prénom est obligatoire pour un particulier");
      } else if (!nameRegex.test(formData.prenom_client.trim())) {
        validationErrors.push("Le prénom ne doit contenir que des lettres");
      }
      if (!formData.piece_identite_client.trim()) {
        validationErrors.push("La pièce d'identité est obligatoire pour un particulier");
      }
    } else if (formData.type_client === "1") { // Entreprise
      if (!formData.nif_client.trim()) {
        validationErrors.push("Le NIF est obligatoire pour une entreprise");
      }
      if (!formData.rccm_client.trim()) {
        validationErrors.push("Le RCCM est obligatoire pour une entreprise");
      }
    }
    
    // Validation email
    if (formData.email_client && !/\S+@\S+\.\S+/.test(formData.email_client)) {
      validationErrors.push("Format d'email invalide");
    }
    
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 handleSubmit appelé");
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      console.log("❌ Erreurs de validation:", validationErrors);
      setErrors(validationErrors);
      return;
    }
    
    console.log("✅ Validation OK");
    setLoading(true);
    setErrors([]);
    setSuccess("");
    setQuotaError({ show: false });
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.log("❌ Token manquant");
        throw new Error("Session expirée. Veuillez vous reconnecter.");
      }
      
      // Préparer les données avec le téléphone formaté
      const formDataToSend = {
        ...formData,
        // Formatage du téléphone avec indicatif
        tel_client: formatFullPhoneNumber(
          extractPhoneDigits(formData.tel_client), 
          selectedCountry?.code || "+227"
        )
      };
      
      console.log("📤 Envoi des données au backend:", JSON.stringify(formDataToSend, null, 2));
      console.log("🌐 URL:", `${API_BASE_URL}/clients/create.php`);
      console.log("🔑 Token présent:", !!token);
      
      const response = await axios.post(
        `${API_BASE_URL}/clients/create.php`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      console.log("📦 Réponse du backend - status:", response.status);
      console.log("📦 Réponse du backend - data:", response.data);
      
      if (response.data.success) {
        console.log("✅ Succès création client");
        setSuccess("Client créé avec succès !");
        
        // Redirection après 2 secondes
        setTimeout(() => {
          console.log("🔄 Redirection vers /clients");
          navigate("/clients");
        }, 2000);
      } else {
        console.log("❌ Erreur backend:", response.data.message);
        setErrors([response.data.message || "Erreur lors de la création"]);
      }
    } catch (error) {
      console.error("❌ Erreur complète:", error);
      
      if (error.response) {
        console.error("📊 Réponse d'erreur status:", error.response.status);
        console.error("📊 Réponse d'erreur data:", error.response.data);
        console.error("📊 Réponse d'erreur headers:", error.response.headers);
        
        // 🔍 Vérifier si c'est une erreur de quota
        if (error.response.status === 403 && error.response.data?.code === 'QUOTA_DEPASSE') {
          console.log("⚠️ Quota dépassé détecté");
          const data = error.response.data;
          setQuotaError({
            show: true,
            type: data.type,
            message: data.message,
            utilises: data.utilises || 0,
            total: data.total || 0,
            restant: data.restant || 0
          });
        }
        // Vérifier si c'est une erreur d'abonnement
        else if (error.response.status === 403 && error.response.data?.code === 'ABONNEMENT_REQUIS') {
          console.log("⚠️ Abonnement requis détecté");
          // Message avec bouton de souscription
          setErrors([
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {error.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setErrors([])}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          ]);
        } else if (error.response.status === 401) {
          console.log("⚠️ Session expirée");
          setErrors(["Session expirée. Veuillez vous reconnecter."]);
          localStorage.clear();
          setTimeout(() => navigate("/login"), 2000);
        } else if (error.response.status === 403) {
          console.log("⚠️ Accès interdit");
          setErrors(["Accès interdit. Vous n'avez pas les permissions nécessaires."]);
        } else if (error.response.status === 400 || error.response.status === 409) {
          console.log("⚠️ Erreur de validation ou conflit");
          // Erreurs de validation ou conflit
          if (error.response.data.errors) {
            console.log("📋 Erreurs de validation:", error.response.data.errors);
            setErrors(error.response.data.errors);
          } else {
            console.log("📋 Message d'erreur:", error.response.data.message);
            setErrors([error.response.data.message || "Erreur de validation"]);
          }
        } else {
          console.log("⚠️ Autre erreur HTTP");
          setErrors([`Erreur ${error.response.status}: ${error.response.data.message || "Erreur technique"}`]);
        }
      } else if (error.request) {
        console.log("❌ Pas de réponse du serveur");
        setErrors(["Serveur indisponible. Veuillez réessayer plus tard."]);
      } else {
        console.log("❌ Erreur de configuration:", error.message);
        setErrors([error.message || "Erreur inconnue"]);
      }
    } finally {
      setLoading(false);
      console.log("🏁 Fin du traitement");
    }
  };

  const handleReset = () => {
    console.log("🔄 Reset formulaire");
    setFormData({
      nom_client: "",
      prenom_client: "",
      tel_client: "",
      email_client: "",
      type_client: "1",
      date_debut_contrat_client: "",
      date_fin_contrat_client: "",
      piece_identite_client: "",
      nif_client: "",
      rccm_client: "",
      statut_client: "1",
      jours_rappel_fin_contrat: "5"
    });
    setErrors([]);
    setSuccess("");
    setQuotaError({ show: false });
  };

  // Si en train de vérifier les permissions
  if (checkingPermissions) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Clients</h1>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-2" style={{ color: COLORS.darkText }}>Vérification des permissions...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Si pas d'utilisateur (après vérification)
  if (!userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Clients</h1>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Erreur</h5>
              <p style={{ color: COLORS.darkText }}>Impossible de charger les informations utilisateur.</p>
              <button
                className="btn mt-2 fw-semibold"
                onClick={() => navigate("/login")}
                style={{
                  color: 'white',
                  backgroundColor: COLORS.primary,
                  borderColor: COLORS.primary,
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.secondary;
                  e.target.style.borderColor = COLORS.secondary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.borderColor = COLORS.primary;
                }}
              >
                Se reconnecter
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Créer un client</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/clients">Clients</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Nouveau client</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-person-plus me-2" style={{ color: COLORS.secondary }}></i>
                  Créer un nouveau client
                </h5>

                {/* Quota restant selon l'abonnement — informatif, affiché avant
                    même de soumettre (le blocage réel se fait toujours côté
                    backend, voir le message d'erreur de quota ci-dessous). */}
                {quotaInfo && !quotaError.show && (
                  <div className="alert mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{
                    backgroundColor: quotaInfo.restant <= 0 ? '#ffebee' : (quotaInfo.pourcentage >= 90 ? '#fff3e0' : '#E3F2FD'),
                    borderLeft: `4px solid ${quotaInfo.restant <= 0 ? COLORS.danger : (quotaInfo.pourcentage >= 90 ? COLORS.warning : COLORS.secondary)}`,
                    borderRadius: '8px'
                  }}>
                    <div>
                      <i className="bi bi-people me-2" style={{ color: COLORS.primary }}></i>
                      <strong>{quotaInfo.utilises} sur {quotaInfo.total}</strong> clients utilisés
                      {' '}({quotaInfo.restant > 0 ? `${quotaInfo.restant} restant${quotaInfo.restant > 1 ? 's' : ''}` : 'quota atteint'})
                    </div>
                    {quotaInfo.pourcentage >= 90 && (
                      <a href="/achat-quota?type=client" className="btn btn-sm fw-semibold" style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '6px', textDecoration: 'none' }}>
                        <i className="bi bi-cart-plus me-1"></i> Acheter plus de clients
                      </a>
                    )}
                  </div>
                )}

                {/* Message d'erreur de quota */}
                {quotaError.show && (
                  <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#fff3e0',
                    borderColor: COLORS.warning,
                    color: '#ff8f00',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.warning}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                      <div className="flex-grow-1">
                        {quotaError.message}
                        <div className="mt-3">
                          <div className="d-flex align-items-center justify-content-center mb-3">
                            <div className="text-center me-4">
                              <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                                {quotaError.utilises}
                              </div>
                              <small>Utilisés</small>
                            </div>
                            <div className="text-center me-4">
                              <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                                {quotaError.total}
                              </div>
                              <small>Total</small>
                            </div>
                            <div className="text-center">
                              <div className="fw-bold text-danger" style={{ fontSize: '1.2rem' }}>
                                {quotaError.restant}
                              </div>
                              <small>Restants</small>
                            </div>
                          </div>
                          
                          <div className="progress mb-3" style={{ height: '10px' }}>
                            <div 
                              className="progress-bar bg-warning" 
                              role="progressbar" 
                              style={{ 
                                width: `${(quotaError.utilises / quotaError.total) * 100}%` 
                              }}
                            ></div>
                          </div>

                          <div className="d-flex gap-2 justify-content-center">
                            <a 
                              href={`/achat-quota?type=${quotaError.type}`}
                              className="btn fw-semibold"
                              style={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px 20px',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                            >
                              <i className="bi bi-cart-plus"></i>
                              Acheter plus de clients
                            </a>
                            <button
                              className="btn fw-semibold"
                              onClick={() => setQuotaError({ show: false })}
                              style={{
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px 20px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                            >
                              Fermer
                            </button>
                          </div>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setQuotaError({ show: false })}
                      ></button>
                    </div>
                  </div>
                )}

                {/* Messages d'erreur */}
                {errors.length > 0 && !quotaError.show && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                      <div className="flex-grow-1">
                        <strong>Des erreurs ont été détectées :</strong>
                        <ul className="mb-0 mt-2">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setErrors([])}
                      ></button>
                    </div>
                  </div>
                )}

                {/* Message de succès */}
                {success && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div className="flex-grow-1">
                        {success}
                        <div className="mt-2">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Redirection vers la liste des clients...
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccess("")}
                      ></button>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Section Type, Statut et Rappel - Sur la même ligne */}
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-gear"></i>
                        </div>
                        Configuration du client
                      </h6>
                      
                      <div className="row">
                        {/* Type de client */}
                        <div className="col-md-4 mb-3">
                          <label htmlFor="type_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-person-badge me-1" style={{ color: COLORS.secondary }}></i>
                            Type de client <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-people"></i>
                            </span>
                            <select
                              id="type_client"
                              name="type_client"
                              className="form-select"
                              value={formData.type_client}
                              onChange={handleChange}
                              required
                              disabled={loading}
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            >
                              <option value="1">Entreprise</option>
                              <option value="2">Particulier</option>
                            </select>
                          </div>
                        </div>

                        {/* Statut */}
                        <div className="col-md-4 mb-3">
                          <label htmlFor="statut_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-check-circle me-1" style={{ color: COLORS.secondary }}></i>
                            Statut du client
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-activity"></i>
                            </span>
                            <select
                              id="statut_client"
                              name="statut_client"
                              className="form-select"
                              value={formData.statut_client}
                              onChange={handleChange}
                              disabled={loading}
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            >
                              <option value="1">Actif</option>
                              <option value="0">Inactif</option>
                              <option value="2">Suspendu</option>
                            </select>
                          </div>
                        </div>

                        {/* Jours de rappel */}
                        <div className="col-md-4 mb-3">
                          <label htmlFor="jours_rappel_fin_contrat" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-bell me-1" style={{ color: COLORS.secondary }}></i>
                            Rappel avant fin (jours) <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-clock"></i>
                            </span>
                            <input
                              type="number"
                              id="jours_rappel_fin_contrat"
                              name="jours_rappel_fin_contrat"
                              className="form-control"
                              min="1"
                              max="30"
                              value={formData.jours_rappel_fin_contrat || 5}
                              onChange={handleChange}
                              required
                              disabled={loading}
                              placeholder="5"
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                            <span className="input-group-text" style={{ 
                              backgroundColor: '#f8f9fa',
                              color: '#6c757d',
                              fontSize: '0.85rem'
                            }}>
                              jours
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        {formData.type_client === "1" 
                          ? "Pour une entreprise, le NIF et RCCM sont obligatoires." 
                          : "Pour un particulier, le prénom et la pièce d'identité sont obligatoires."}
                      </div>
                    </div>
                  </div>

                  {/* Section Informations personnelles */}
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-person-vcard"></i>
                        </div>
                        Informations personnelles
                      </h6>
                      
                      <div className="row">
                        {/* Nom */}
                        <div className="col-md-6 mb-3">
                          <label htmlFor="nom_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-person me-1" style={{ color: COLORS.secondary }}></i>
                            {formData.type_client === "2" ? "Nom (Nom de famille)" : "Nom (Raison sociale)"} <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-person-vcard"></i>
                            </span>
                            <input
                              type="text"
                              id="nom_client"
                              name="nom_client"
                              className="form-control"
                              value={formData.nom_client}
                              onChange={handleChange}
                              required
                              disabled={loading}
                              placeholder={formData.type_client === "1" ? "Raison sociale" : "Nom de famille"}
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                        </div>

                        {/* Prénom / Nom commercial */}
                        <div className="col-md-6 mb-3">
                          <label htmlFor="prenom_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-person-bounding-box me-1" style={{ color: COLORS.secondary }}></i>
                            {formData.type_client === "2" ? "Prénom" : "Nom commercial"} 
                            {formData.type_client === "2" && <span className="text-danger">*</span>}
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-person"></i>
                            </span>
                            <input
                              type="text"
                              id="prenom_client"
                              name="prenom_client"
                              className="form-control"
                              value={formData.prenom_client}
                              onChange={handleChange}
                              required={formData.type_client === "2"}
                              disabled={loading}
                              placeholder={formData.type_client === "1" ? "Nom commercial (optionnel)" : "Prénom"}
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        {/* Téléphone avec sélecteur de pays */}
                        <div className="col-md-6 mb-3">
                          <label htmlFor="tel_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-phone me-1" style={{ color: COLORS.secondary }}></i>
                            Téléphone <span className="text-danger">*</span>
                          </label>
                          
                          <div className="d-flex align-items-stretch" style={{ gap: '8px' }}>
                            <CountrySelector
                              showList={showCountryList}
                              setShowList={setShowCountryList}
                              selectedCountry={selectedCountry}
                              setSelectedCountry={setSelectedCountry}
                              search={searchCountry}
                              setSearch={setSearchCountry}
                              listRef={countryListRef}
                              disabled={loading}
                            />
                            
                            <div className="position-relative" style={{ flex: '1' }}>
                              <div style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#666',
                                fontSize: '0.95rem',
                                pointerEvents: 'none',
                                zIndex: 5
                              }}>
                                {selectedCountry?.code || "+227"}
                              </div>
                              <input
                                type="tel"
                                id="tel_client"
                                name="tel_client"
                                className="form-control"
                                value={formData.tel_client}
                                onChange={handleChange}
                                placeholder={selectedCountry?.phoneLength ? 
                                  `Ex: ${generatePhoneExample(selectedCountry.phoneLength)}` : 
                                  "Numéro de téléphone"}
                                disabled={loading}
                                maxLength={selectedCountry?.phoneLength || 15}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                required
                                style={{ 
                                  borderRadius: '8px',
                                  borderColor: '#e0e0e0',
                                  padding: '12px 16px 12px 55px',
                                  fontSize: '0.95rem',
                                  width: '100%',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>
                          
                          <small className="text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>
                            Format: {selectedCountry?.code || "+227"} + {selectedCountry?.phoneLength || "?"} chiffres
                            {selectedCountry?.phoneLength && 
                              ` (ex: ${selectedCountry.code}${generatePhoneExample(selectedCountry.phoneLength)})`}
                          </small>
                        </div>

                        {/* Email */}
                        <div className="col-md-6 mb-3">
                          <label htmlFor="email_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-envelope me-1" style={{ color: COLORS.secondary }}></i>
                            Email
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-at"></i>
                            </span>
                            <input
                              type="email"
                              id="email_client"
                              name="email_client"
                              className="form-control"
                              value={formData.email_client}
                              onChange={handleChange}
                              disabled={loading}
                              placeholder="client@exemple.com"
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section spécifique selon le type */}
                  {formData.type_client === "1" ? (
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-body">
                        <h6 className="card-title mb-4" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-building"></i>
                          </div>
                          Informations entreprise
                        </h6>
                        
                        <div className="row">
                          {/* NIF - Entreprise */}
                          <div className="col-md-6 mb-3">
                            <label htmlFor="nif_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-file-text me-1" style={{ color: COLORS.secondary }}></i>
                              NIF (Numéro d'Identification Fiscale) <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-file-earmark-text"></i>
                              </span>
                              <input
                                type="text"
                                id="nif_client"
                                name="nif_client"
                                className="form-control"
                                value={formData.nif_client}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                placeholder="NIF-XXXXXXXXX"
                                style={{
                                  borderColor: '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>

                          {/* RCCM - Entreprise */}
                          <div className="col-md-6 mb-3">
                            <label htmlFor="rccm_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-file-text me-1" style={{ color: COLORS.secondary }}></i>
                              RCCM (Registre de Commerce) <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-file-earmark-text"></i>
                              </span>
                              <input
                                type="text"
                                id="rccm_client"
                                name="rccm_client"
                                className="form-control"
                                value={formData.rccm_client}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                placeholder="CD/XXX/XXXXX"
                                style={{
                                  borderColor: '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="card mb-4" style={{ 
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.lightBg}`,
                      boxShadow: 'none'
                    }}>
                      <div className="card-body">
                        <h6 className="card-title mb-4" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600'
                        }}>
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                            width: '30px',
                            height: '30px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-person-badge"></i>
                          </div>
                          Informations particulier
                        </h6>
                        
                        <div className="row">
                          {/* Pièce d'identité - Particulier */}
                          <div className="col-md-6 mb-3">
                            <label htmlFor="piece_identite_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-credit-card me-1" style={{ color: COLORS.secondary }}></i>
                              Pièce d'identité <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-card-text"></i>
                              </span>
                              <input
                                type="text"
                                id="piece_identite_client"
                                name="piece_identite_client"
                                className="form-control"
                                value={formData.piece_identite_client}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                placeholder="N° Passeport ou Carte d'identité"
                                style={{
                                  borderColor: '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>

                          {/* NIF - Optionnel pour particulier */}
                          <div className="col-md-6 mb-3">
                            <label htmlFor="nif_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              <i className="bi bi-file-text me-1" style={{ color: COLORS.secondary }}></i>
                              NIF (optionnel)
                            </label>
                            <div className="input-group">
                              <span className="input-group-text" style={{ 
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.secondary
                              }}>
                                <i className="bi bi-file-earmark-text"></i>
                              </span>
                              <input
                                type="text"
                                id="nif_client"
                                name="nif_client"
                                className="form-control"
                                value={formData.nif_client}
                                onChange={handleChange}
                                disabled={loading}
                                placeholder="NIF-XXXXXXXXX"
                                style={{
                                  borderColor: '#e0e0e0',
                                  borderRadius: '0 8px 8px 0',
                                  color: COLORS.darkText
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section Dates de contrat */}
                  <div className="card mb-4" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: 'none'
                  }}>
                    <div className="card-body">
                      <h6 className="card-title mb-4" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                          width: '30px',
                          height: '30px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-calendar-check"></i>
                        </div>
                        Dates du contrat
                      </h6>
                      
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="date_debut_contrat_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
                            Date de début du contrat <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-calendar-date"></i>
                            </span>
                            <input
                              type="date"
                              id="date_debut_contrat_client"
                              name="date_debut_contrat_client"
                              className="form-control"
                              value={formData.date_debut_contrat_client}
                              onChange={handleChange}
                              required
                              disabled={loading}
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label htmlFor="date_fin_contrat_client" className="form-label" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            <i className="bi bi-calendar-minus me-1" style={{ color: COLORS.secondary }}></i>
                            Date de fin du contrat <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.lightBg,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-calendar-x"></i>
                            </span>
                            <input
                              type="date"
                              id="date_fin_contrat_client"
                              name="date_fin_contrat_client"
                              className="form-control"
                              value={formData.date_fin_contrat_client}
                              onChange={handleChange}
                              required
                              disabled={loading}
                              style={{
                                borderColor: '#e0e0e0',
                                borderRadius: '0 8px 8px 0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Boutons */}
                  <div className="d-flex justify-content-between mt-4 pt-4 border-top" style={{ 
                    borderColor: COLORS.lightBg
                  }}>
                    <button
                      type="button"
                      className="btn fw-semibold"
                      onClick={() => navigate("/clients")}
                      disabled={loading}
                      style={{
                        color: COLORS.darkText,
                        borderColor: '#e0e0e0',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.target.style.backgroundColor = COLORS.lightBg;
                          e.target.style.borderColor = COLORS.primary;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = '#e0e0e0';
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i> Retour
                    </button>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={handleReset}
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.lightBg;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-x-circle me-2"></i> Effacer
                      </button>

                      <button
                        type="submit"
                        className="btn fw-semibold"
                        disabled={loading}
                        style={{
                          color: 'white',
                          backgroundColor: loading ? '#B0BEC5' : COLORS.primary,
                          borderColor: loading ? '#B0BEC5' : COLORS.primary,
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease',
                          opacity: loading ? 0.8 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.secondary;
                            e.target.style.borderColor = COLORS.secondary;
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.borderColor = COLORS.primary;
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Création en cours...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i> Créer le client
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .form-select,
        .input-group-text,
        .form-control {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:disabled,
        .form-select:disabled {
          background-color: ${COLORS.lightBg};
          cursor: not-allowed;
        }
        
        .input-group-text {
          background-color: ${COLORS.lightBg};
          border-color: #dee2e6;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ClientCreate;